import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { newsList } from "@/mocks/news";
import { getNewsById, formatDate } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const news = id ? getNewsById(id) : undefined;

  const related = news
    ? newsList.filter((n) => n.id !== news.id).slice(0, 3)
    : [];

  usePageMeta({
    title: news ? `${news.title} - po18娱乐资讯` : "资讯详情 - po18",
    description: news ? news.summary : "po18 娱乐资讯详情页。",
    keywords: news ? `po18,${news.category},娱乐资讯` : "po18,娱乐资讯",
  });

  if (!news) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-file-search-line" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-900">
            未找到该资讯
          </h1>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-lg" />
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article className="w-full px-4 md:px-6">
        <nav className="pt-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/news" className="hover:text-primary-600 transition-colors cursor-pointer">
            娱乐资讯
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 font-medium truncate max-w-[40vw]">
            {news.category}
          </span>
        </nav>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
              <i className="ri-price-tag-3-line" />
              {news.category}
            </span>
            <h1 className="mt-4 text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
              {news.title}
            </h1>
            <p className="mt-3 text-sm text-foreground-400">
              发布于 {formatDate(news.date)}
            </p>

            <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src={news.image}
                alt={`${news.title} 配图`}
                title={`${news.title} - po18 娱乐资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6 text-base text-foreground-700 leading-loose space-y-4">
              <p className="font-medium text-foreground-900">{news.summary}</p>
              <p>{news.content}</p>
            </div>

            <Link
              to="/news"
              className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-lg" />
              返回资讯列表
            </Link>
          </div>

          <aside className="min-w-0">
            <div className="lg:sticky lg:top-20 rounded-lg bg-background-100 border border-background-200/70 p-5">
              <h2 className="text-base font-bold text-foreground-900 mb-4">
                更多资讯
              </h2>
              <ul className="space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/${item.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.image}
                          alt={`${item.title} 配图`}
                          title={`${item.title} - po18`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </p>
                        <p className="mt-1 text-xs text-foreground-400">
                          {formatDate(item.date)}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </article>
    </SiteLayout>
  );
}